/* eslint-disable max-lines, no-underscore-dangle, max-lines-per-function */
import { EventBus, Events } from "jiffy-store";

import events from "common/events";
import { CachedDeliveryManager } from "common/utils/delivery-date/cached-delivery-manager";
import {
  fetchPdpDeliveryEstimates,
  isFastDeliveryEstimateEnabled
} from "common/utils/delivery-date/fetch-pdp-delivery-estimates";
import {
  cacheProductColorDeliveryEstimate,
  findCacheProductColorDeliveryEstimate,
  buildDataName
} from "components/jiffy/product-details/cache-composition-delivery-date";
import {
  shouldShowTimeRange,
  formatDeliveryDateForDisplay,
  getEarliestDeliveryDate
} from "common/utils/format/date";
import { moneyFormat } from "common/utils/filters/money";
import initSameDayBadge from "components/jiffy/same-day-badge/same-day-badge";
import initDeliveryDatePills from "components/jiffy/delivery-date-pill/delivery-date-pill";
import {
  normalizeCompositionName,
  resolveCompositionName
} from "common/utils/delivery-date/resolve-composition-name";
import api from "common/utils/api";

import { isDeliveryNonEstimatable } from "./utils";
import selectors from "./selectors";
import {
  sendPDPDeliveryDateChangeTracking,
  sendPDPDeliveryDateRenderedTracking,
  trackProductViewed,
  trackDeliveryDateApiFailed
} from "./track";

const $getActiveColor = () => document.getElementById(selectors.global.ACTIVE_COLOR_NAME);
const $getCutoffCollection = () => document.querySelectorAll(selectors.global.CUTOFF);
const $getDeliveryDates = () => document.querySelector(selectors.global.DELIVERY_DATES);
const $getModalDeliveryDates = () => document.querySelector(selectors.global.DELIVERY_DATES);
const $getModalShipmentList = () => document.querySelector(selectors.global.MODAL_SHIPMENTS_LIST);
const $getColorVariant = color =>
  document.querySelector(
    `${selectors.global.COLOR_VARIANT_SELECTOR}[data-color="${CSS.escape(color)}"]`
  );
const $getModalSpinner = () => document.querySelector(selectors.global.MODAL_SPINNER);

const $getContainer = () =>
  document.getElementById(selectors.local.CONTAINER) ||
  document.getElementById(selectors.made.CONTAINER);
const $getFlashThresholdContainer = () => document.querySelector(selectors.local.PDP_CONTAINER);
const $getSpinners = () => document.querySelectorAll(selectors.local.SPINNER);
const $getWrappers = () => document.querySelectorAll(selectors.local.WRAPPER);
const $getDirectDeliveryDates = () =>
  document.querySelectorAll(selectors.local.DIRECT_DELIVERY_DATE);
const $getQuantityForm = () => document.querySelector(".js-product-details-quantity-form");
const $getQuantityInputs = $form => $form.getElementsByClassName("js-quantity-form-input");
const $getShippingProgressBarWrappers = () =>
  document.querySelectorAll(".js-pdp-shipping-progress-bar-wrapper");
const $getAddToCartShippingInfo = () =>
  document.querySelector(".js-continue-shopping-delivery-dates");
const $getProgressBarFreeShipping = $wrapper =>
  $wrapper.querySelector(".js-pdp-shipping-progress-bar-complete");
const $getProgressBarShortfall = $wrapper =>
  $wrapper.querySelector(".js-pdp-shipping-progress-bar-shortfall");
const $getProgressBarAmount = $wrapper =>
  $wrapper.querySelector(".js-pdp-shipping-progress-bar-amount");
const $getProgressBar = $wrapper => $wrapper.querySelector(".js-pdp-shipping-progress-bar");
const $getProgressBarLine = $wrapper =>
  $wrapper.querySelector(".js-pdp-shipping-progress-bar-line");
const $getProgressBarThreshold = $wrapper =>
  $wrapper.querySelector(".js-pdp-shipping-progress-bar-threshold");

const $getDtfFreeShipping = () => document.querySelector(".js-dtf-free-shipping");

const $getProductDetailsDtfExtras = () => document.querySelector(".js-product-details-dtf-extras");
const $getProductDetailsUserFeedback = () =>
  document.querySelector(".js-product-details-user-feedback");

const $getBuyItAgainModal = () => document.getElementById("js-buy-it-again-modal");
const $getBuyItAgainDtfModal = () => document.getElementById("buy-it-again-dtf-modal");

let dtfPricesDecorator;

export const deliveryDateResponse = {
  _lastResponse: undefined,
  _useLastFetchResponse: false,
  get useLastFetchResponse() {
    return this._useLastFetchResponse && this._lastResponse;
  },
  set useLastFetchResponse(value) {
    this._useLastFetchResponse = value;
  },
  get lastResponse() {
    return this._lastResponse;
  },
  set lastResponse(value) {
    this._lastResponse = value;
  },
  reloadDeliveryEstimatesWithoutFetch({ pdpSubtotal } = {}) {
    this.useLastFetchResponse = true;
    reloadDeliveryEstimates([], false, { pdpSubtotal, refreshCartShipping: true });
    this.useLastFetchResponse = false;
  }
};

const addHighLight = $elm => {
  $elm.classList.add(selectors.global.DATE_HIGHLIGHT);
  setTimeout(() => {
    $elm.classList.remove(selectors.global.DATE_HIGHLIGHT);
  }, 500);
};

const insertDate = (dates, $dateNode) => {
  const $date = $dateNode;
  const [singleDate] = dates.value_full.split(" & ");

  $date.innerHTML = formatDeliveryDateForDisplay(singleDate);

  const deliveryDate = getEarliestDeliveryDate(dates.shipments);
  if (deliveryDate) {
    $date.dataset.deliveryDate = deliveryDate;
  }

  addHighLight($date);
};

const toggleSameDayShippingInfo = show => {
  $getContainer()?.classList.toggle("product-details-delivery-date-simple--same-day", show);
  $getAddToCartShippingInfo()?.classList.toggle(
    "continue-shopping-modal__delivery-dates--same-day",
    show
  );
};

const initSameDayPDPBadges = (showBadge, flashCutOffTime, deliveryDate) => {
  [$getProductDetailsDtfExtras(), $getProductDetailsUserFeedback()].forEach(el => {
    if (el) {
      const badge = initSameDayBadge(el, {
        pageType: "pdp",
        flashCutOffTime,
        deliveryDate
      });

      if (badge) {
        if (showBadge && badge.isFlashDelivery) badge.showBadge();
        else badge.hideBadge();
      }
    }
  });
};

const hasValidCartDates = cartDates => cartDates?.value_full;

const supportsCartAwareDates = () => {
  if (!isFastDeliveryEstimateEnabled()) {
    const isDtf = $getContainer()?.dataset?.isDtf === "true";
    return !isDtf;
  }
  return true;
};

const insertDeliveryDates = (
  { cart_dates: cartDates, composition_dates: compositionDates },
  isSameDayDelivery
) => {
  const useCartDates = supportsCartAwareDates() && hasValidCartDates(cartDates);
  const displayDates = useCartDates ? cartDates : compositionDates;
  $getDirectDeliveryDates().forEach(insertDate.bind(this, displayDates));

  toggleSameDayShippingInfo(isSameDayDelivery);
  initSameDayPDPBadges(isSameDayDelivery, displayDates.timeleft, displayDates.value_full);

  // Initialize delivery date pills
  initDeliveryDatePills();

  /* continue-shopping-modal */
  /* @todo: move to the correct place */
  const shipmentsForModal = cartDates?.shipments || compositionDates?.shipments;
  if (shipmentsForModal) {
    $getDeliveryDates()?.classList?.remove("hidden");
    $getModalShipmentList().innerHTML = shipmentsForModal
      .map(({ date }) => {
        const displayDate = formatDeliveryDateForDisplay(date);
        return `<div class="continue-shopping-modal__delivery-dates-item">${displayDate}</div>`;
      })
      .join("");
  }
};

export const setPDPDatesLoading = isLoading => {
  $getSpinners()?.forEach($spinner => $spinner?.classList.toggle("shown", isLoading));
  $getWrappers()?.forEach($wrapper => $wrapper?.classList.remove("hidden"));

  $getModalDeliveryDates()?.classList?.toggle("hidden", isLoading);
  $getModalSpinner()?.classList?.toggle("continue-shopping-modal__loading--visible", isLoading);

  // Emit events for delivery date pill
  if (isLoading) {
    EventBus.publish(events.DELIVERY_DATE_LOADING_START, null);
  } else {
    EventBus.publish(events.DELIVERY_DATE_LOADING_END, null);
  }
};

const isActiveProductAssignable = () => {
  const $container = $getContainer();

  // First priority: explicit assignable attribute (used in modals and DTF products)
  if ($container?.dataset?.assignable !== undefined) {
    return $container.dataset.assignable === "true";
  }

  // Second priority: DTF products should default to assignable
  const isDtfProduct = $container?.dataset?.isDtf === "true";
  if (isDtfProduct) {
    return true;
  }

  // Third priority: regular product logic requiring active color
  const $activeColorElement = $getActiveColor();
  if (!$activeColorElement) return false;

  const { value: color } = $activeColorElement;
  const $colorVariantSelector = $getColorVariant(color);

  if (!$colorVariantSelector) return false;
  return $colorVariantSelector.getAttribute("data-show-estimates") === "true";
};

const fetchDeliveryDate = async (productId, compositionName, payload) => {
  if (!isActiveProductAssignable()) return [{}, 0];

  const normalizedCompositionName = normalizeCompositionName(compositionName);

  // Validate that we have valid product and composition data before making API calls
  if (!productId || !normalizedCompositionName) {
    console.warn("fetchDeliveryDate: Missing productId or compositionName", {
      productId,
      compositionName: normalizedCompositionName
    });
    return [{}, 0];
  }

  const skipCache = payload.skip_cache || false;
  delete payload.skip_cache;

  const cachedData = findCacheProductColorDeliveryEstimate(productId, normalizedCompositionName);

  if (cachedData && !skipCache) return [cachedData, 0];

  const startedAt = performance?.now() || 0;

  const apiPayload = { ...payload, composition_name: normalizedCompositionName };

  let data;
  try {
    data = deliveryDateResponse.useLastFetchResponse
      ? deliveryDateResponse.lastResponse
      : await fetchPdpDeliveryEstimates({ productId }, apiPayload);
    deliveryDateResponse.lastResponse = data;
    data = dtfPricesDecorator?.updateCostInfo(data) || data;
  } catch (error) {
    trackDeliveryDateApiFailed({
      productId,
      compositionName: normalizedCompositionName,
      payload: apiPayload,
      error
    });
    console.warn("fetchDeliveryDate: API call failed", {
      productId,
      compositionName: normalizedCompositionName,
      error
    });
    return [{}, 0];
  }

  const finishedAt = performance?.now() || 0;

  const $activeColorElement = $getActiveColor();
  if ($activeColorElement) {
    const { value: activeColor } = $activeColorElement;
    const normalizedActiveColor = normalizeCompositionName(activeColor);
    if (normalizedActiveColor === normalizedCompositionName) {
      $getContainer().classList.toggle("hidden", !data.success);
    }
  }
  if (!data.success) return [{}, 0];

  if (!skipCache) {
    const name = buildDataName(productId, normalizedCompositionName);
    const useCartDates = supportsCartAwareDates() && hasValidCartDates(data.cart_dates);
    const cacheDates = useCartDates ? data.cart_dates : data.composition_dates;
    if (!cacheDates?.value_full) return [data, finishedAt - startedAt];
    const cacheData = [name, cacheDates.value_full, cacheDates.timeleft, data.mixpanel];

    cacheProductColorDeliveryEstimate(productId, normalizedCompositionName, cacheData);
  }
  return [data, finishedAt - startedAt];
};

const updateSingleProgressBar = ($progressBarWrapper, shippingCostData, isSameDayDelivery) => {
  $progressBarWrapper.classList.remove("hidden");
  if (isSameDayDelivery != null) {
    $getFlashThresholdContainer()?.classList.toggle("hidden", !isSameDayDelivery);
  }
  const $freeShipping = $getProgressBarFreeShipping($progressBarWrapper);
  const $shippingShortfall = $getProgressBarShortfall($progressBarWrapper);

  if (shippingCostData.free) {
    $freeShipping.classList.remove("hidden");
    $shippingShortfall.classList.add("hidden");
    return;
  }

  const $progressBar = $getProgressBar($progressBarWrapper);
  const { currencySign } = $progressBar.dataset;

  const $progressBarAmount = $getProgressBarAmount($progressBarWrapper);
  const $progressBarLine = $getProgressBarLine($progressBarWrapper);
  const $progressBarThreshold = $getProgressBarThreshold($progressBarWrapper);

  const threshold = shippingCostData.free_shipping_threshold;
  const shortfallAmount = shippingCostData.amount_until_free;
  const percentageReached = shippingCostData.percentage_reached;

  $progressBarThreshold.textContent = moneyFormat(threshold, currencySign);
  $progressBarAmount.textContent = moneyFormat(shortfallAmount, currencySign);
  $progressBarLine.style.width = `${percentageReached}%`;

  $freeShipping.classList.add("hidden");
  $shippingShortfall.classList.remove("hidden");
};

const updatePDPShippingProgressBars = (shippingCostData, isSameDayDelivery) => {
  if (!shippingCostData) return;

  const $progressBarWrappers = $getShippingProgressBarWrappers();

  if (!$progressBarWrappers.length) return;

  $progressBarWrappers.forEach($wrapper =>
    updateSingleProgressBar($wrapper, shippingCostData, isSameDayDelivery)
  );
};

const collectVariantsFromForm = () => {
  const $form = $getQuantityForm();
  if (!$form) return null;

  const variants = Array.from($getQuantityInputs($form)).reduce((acc, input) => {
    const quantity = Number(input.value);
    const { variantId } = input.dataset;
    if (quantity > 0 && variantId) acc[variantId] = (acc[variantId] || 0) + quantity;
    return acc;
  }, {});

  return Object.keys(variants).length > 0 ? JSON.stringify(variants) : null;
};

const readPdpSubtotal = () => {
  const $el = document.querySelector(".summary__row--subtotal .summary__value");
  if (!$el) return null;
  const text = $el.textContent?.trim()?.replace(/[^0-9.]/g, "");
  if (!text) return null;
  const amount = parseFloat(text);
  return Number.isFinite(amount) ? amount : null;
};

const fetchAndUpdateCartShippingInfo = async (productId, compositionName, { pdpSubtotal } = {}) => {
  if (!isFastDeliveryEstimateEnabled() || !supportsCartAwareDates()) return;

  try {
    const params = {
      product_id: productId,
      composition_name: compositionName
    };

    const subtotal = pdpSubtotal ?? readPdpSubtotal();
    if (subtotal != null) {
      params.pdp_subtotal = subtotal;
    } else {
      const variantsHash = collectVariantsFromForm();
      if (variantsHash) params.variants_hash = variantsHash;
    }

    // silent: 404 with {success:false} is expected when product has no composition (HB-1851)
    const response = await api.product.fetchCartShippingInfo(params, { silent: true });

    if (response?.success && response?.shipping_cost_info) {
      updatePDPShippingProgressBars(response.shipping_cost_info, null);
    }
  } catch {
    // Shipping progress bar is non-critical; silently degrade
  }
};

const clearExpediteShippingNotes = () => {
  const expediteNotes = document.querySelectorAll(".js-expedite-shipping-note");
  expediteNotes.forEach($note => {
    if ($note) {
      $note.textContent = "";
      const parent = $note.parentElement;
      if (parent) {
        parent.removeAttribute("data-expedite-shipping");
      }
    }
  });
};

// Helper function to update expedite shipping note
const updateExpediteShippingNote = deliveryData => {
  const expediteNotes = document.querySelectorAll(".js-expedite-shipping-note");
  expediteNotes.forEach($note => {
    if ($note) {
      // For PDP estimates, check if it's same-day delivery (expedite)
      // Since estimates don't have category/title, we check if delivery is "Today"
      const deliveryDate = deliveryData?.value_full?.toLowerCase() || "";
      const isExpedite = deliveryDate.includes("today");

      if (isExpedite) {
        $note.textContent = ", with expedite shipping";
        const parent = $note.parentElement;
        if (parent) {
          parent.setAttribute("data-expedite-shipping", "true");
        }
      } else {
        $note.textContent = "";
        const parent = $note.parentElement;
        if (parent) {
          parent.removeAttribute("data-expedite-shipping");
        }
      }
    }
  });
};

// Render the cutoff collection with the server-rendered cutoff label.
// `displayDates.cutoff_label` is computed by `CustomerFacingCutoff.display_label`
// on the Ruby side and already honors the shift / format flags.
const updateCutoffCollection = (hasTimeRange, timeleft, deliveryData) => {
  if (!timeleft) return;

  const cutoffClasses =
    "product-details-delivery-date-simple__cutoff-date product-details-delivery-date-simple__cutoff-date--truck js-product-details-delivery-dates-cutoff";
  const formattedTime = deliveryData?.cutoff_label;
  if (!formattedTime) return;

  // Build HTML for the cutoff message (no expedite shipping note for same-day time range)
  const buildOrderByHTML = `
    <b class="text-center block ${cutoffClasses}">Order by ${formattedTime}</b>
  `;

  // Build HTML for cutoff message (with expedite shipping note support)
  const buildOrderByWithNoteHTML = `
    Order by <b class="${cutoffClasses}">${formattedTime}</b>
    <span class="js-expedite-shipping-note"></span>
  `;

  $getCutoffCollection().forEach($cutoff => {
    const container = $cutoff.parentNode;
    if (hasTimeRange) {
      // Same-day with time range: show cutoff time without expedite note
      container.innerHTML = buildOrderByHTML;
    } else {
      container.innerHTML = buildOrderByWithNoteHTML;
    }
  });

  if (hasTimeRange) {
    clearExpediteShippingNotes();
  } else {
    updateExpediteShippingNote(deliveryData);
  }
};

const toggleDtfFreeShippingNotice = shippingCostData => {
  if (!shippingCostData) return;

  $getDtfFreeShipping()?.classList.toggle("hidden", !shippingCostData.free);
};

export const updateHTML = async (deliveryDates, shouldTrack = true) => {
  const { cart_dates: cartDates, composition_dates: compositionDates } = deliveryDates;
  if (!cartDates?.timeleft && !compositionDates?.timeleft) return;

  const useCartDates = supportsCartAwareDates() && hasValidCartDates(cartDates);
  const displayDates = useCartDates ? cartDates : compositionDates;
  const shippingCostData = displayDates?.shipping_cost_info ?? compositionDates?.shipping_cost_info;
  const hasTimeRange =
    shouldShowTimeRange(displayDates?.value_full) &&
    displayDates?.delivery_time &&
    displayDates?.delivery_time_end;
  const isSameDayDelivery = displayDates.value_full?.toLowerCase().includes("today");

  // Select cutoff source based on cart state - use cart cutoff when cart has shipments
  // Fallback to compositionDates.timeleft if cartDates.timeleft is undefined
  const cutoffSource =
    (cartDates?.shipments?.length > 0 && cartDates?.timeleft) || compositionDates?.timeleft;

  insertDeliveryDates(deliveryDates, isSameDayDelivery);
  updateCutoffCollection(hasTimeRange, cutoffSource, displayDates);
  toggleDtfFreeShippingNotice(shippingCostData);
  updatePDPShippingProgressBars(shippingCostData, isSameDayDelivery);

  if (shouldTrack) {
    await trackProductViewed(deliveryDates);
  }

  // Emit event for delivery date pill update
  EventBus.publish(events.DELIVERY_DATE_UPDATED, deliveryDates);
};

/**
 * The function provides new dates after changing color in the color picker popup or carousel.
 * @param productId {string}
 * @param compositionName {string}
 * @param orderNumber {string}
 */
const loadDeliveryEstimates = async ({
  productId,
  compositionName,
  orderNumber = "",
  useLoader = true,
  skipCache = false
}) => {
  useLoader && setPDPDatesLoading(true);
  const payload = {
    composition_name: compositionName,
    number: orderNumber || undefined,
    skip_cache: skipCache
  };
  const [deliveryDates, ajaxTime] = await fetchDeliveryDate(productId, compositionName, payload);

  useLoader && setPDPDatesLoading(false);
  await updateHTML(deliveryDates, !orderNumber);

  return [deliveryDates, ajaxTime];
};

/**
 * The function provides new dates after updating qty inputs below the delivery dates box.
 * @param productId {string}
 * @param compositionName {string}
 * @param variantString {string}
 * @param orderNumber {string}
 * @param dtfPrintData
 */
const loadDeliveryEstimatesForVariant = async ({
  compositionName,
  orderNumber,
  productId,
  variantString,
  preCut = false,
  dtfPrintData = [],
  useLoader = true,
  designServicesEnabled = false
}) => {
  const performanceStartTime = performance?.now() || 0;
  const $directDeliveryValue = $getDirectDeliveryDates();
  const currentDirectDeliveryDate = {
    text: $directDeliveryValue?.textContent,
    date: $directDeliveryValue?.dataset?.deliveryDate
  };

  useLoader && setPDPDatesLoading(true);
  const payload = {
    composition_name: compositionName,
    number: orderNumber || undefined,
    variants_hash: variantString,
    pre_cut: preCut,
    dtf_print: dtfPrintData,
    design_services_enabled: designServicesEnabled,
    skip_cache: true
  };
  const [deliveryDates, ajaxTime] = await fetchDeliveryDate(productId, compositionName, payload);
  const isDtfPrint = dtfPrintData.length > 0;

  useLoader && setPDPDatesLoading(false);
  await updateHTML(deliveryDates, !orderNumber && !isDtfPrint);

  const performanceEndTime = performance?.now() || 0;
  sendPDPDeliveryDateChangeTracking(currentDirectDeliveryDate, deliveryDates, {
    time_to_get_date: Math.floor(ajaxTime),
    time_to_render_date: Math.floor(performanceEndTime - performanceStartTime),
    page: isDtfPrint ? "transfers-pdp" : "blanks-pdp"
  });
};

export const reloadDeliveryEstimates = async (
  dtfPrintData = [],
  preCut = false,
  { designServicesEnabled = false, pdpSubtotal, refreshCartShipping = false } = {}
) => {
  const $container = $getContainer();
  if (!$container) return;

  const { selectedCart, productId } = $container.dataset;
  const $form = $getQuantityForm();
  if (!$form) return;

  const payload = Array.from($getQuantityInputs($form)).reduce((acc, input) => {
    const quantity = Number(input.value);
    const { variantId } = input.dataset;
    return { ...acc, [variantId]: (acc[variantId] || 0) + quantity };
  }, {});

  const colorName = resolveCompositionName({
    containerSelectedColor: $container.dataset.selectedColor,
    activeColorName: $getActiveColor()?.value
  });
  if (!colorName) return;
  $container.dataset.selectedColor = colorName;

  if (refreshCartShipping) {
    fetchAndUpdateCartShippingInfo(productId, colorName, { pdpSubtotal });
  }

  await loadDeliveryEstimatesForVariant({
    compositionName: colorName,
    orderNumber: selectedCart || "",
    productId,
    variantString: JSON.stringify(payload),
    preCut,
    dtfPrintData,
    designServicesEnabled
  });
};

/**
 * Applies cached delivery date to the product details component.
 * @param {Element} $container - The container element
 * @private
 */
const applyCachedDeliveryDate = $container => {
  const $deliveryDateElements = $container.querySelectorAll(
    ".js-made-product-details-delivery-dates-simple-direct-value, .js-product-details-delivery-dates-simple-direct-value"
  );

  const setLoadingState = isLoading => {
    const $loadingEl = $container.querySelector(
      ".js-made-product-details-delivery-dates-simple-spinner, .js-product-details-delivery-dates-simple-spinner"
    );
    const $loadedEl = $container.querySelector(
      ".js-made-product-details-delivery-dates-simple-wrapper, .js-product-details-delivery-dates-simple-wrapper"
    );

    if ($loadingEl) {
      $loadingEl.classList.toggle("shown", isLoading);
      $loadingEl.classList.toggle("hidden", !isLoading);
    }
    if ($loadedEl) {
      $loadedEl.classList.toggle("hidden", isLoading);
    }
  };

  CachedDeliveryManager.applyCachedDelivery({
    deliveryDateElements: $deliveryDateElements,
    setLoadingState
  });
};

const initDates = async (props = {}) => {
  const $container = $getContainer();
  if (!$container) return;

  const { productId, isDtf } = $container.dataset;
  const isBuyItAgain = $getBuyItAgainModal();
  const isBuyItAgainDtf = $getBuyItAgainDtfModal();
  dtfPricesDecorator = props.dtfPricesDecorator;

  // Removed DTF early-return, it blocked ZIPCODE_UPDATED listeners, and hence price wasn't updated for PDP's
  // and misbehaved outside Buy It Again context.

  // Only proceed if we have a valid product context or are in a modal
  // This prevents trying to fetch delivery dates on pages like /account/orders
  const isValidContext =
    productId &&
    // Product details page context
    (document.getElementById("product-page-container") ||
      // Modal context
      isBuyItAgain ||
      isBuyItAgainDtf);

  if (!isValidContext) return;

  let { selectedColor, selectedCart } = $container.dataset;
  selectedColor = resolveCompositionName({
    containerSelectedColor: selectedColor,
    activeColorName: $getActiveColor()?.value,
    preferQueryParam: true
  });
  if (selectedColor) {
    $container.dataset.selectedColor = selectedColor;
  }

  // Validate we have the required data before proceeding
  if (!selectedColor) {
    console.warn("initDates: Missing selectedColor, skipping delivery date initialization");
    return;
  }

  // Fetch cart shipping info in parallel for blanks PDP
  fetchAndUpdateCartShippingInfo(productId, selectedColor);

  // Check for cached delivery dates first
  if (CachedDeliveryManager.shouldUseCachedDelivery($container.id)) {
    applyCachedDeliveryDate($container);
  } else {
    // Waiting for dates and insert them to the html component
    const [deliveryDates, ajaxTime] = await loadDeliveryEstimates({
      productId,
      compositionName: selectedColor
    });
    const performanceNow = performance?.now() || 0;
    const deliveryDateRenderStart = window?.jiffy?.timers?.deliveryDateRenderStart || 0;
    const renderTime = performanceNow - deliveryDateRenderStart;
    if (deliveryDates) {
      sendPDPDeliveryDateRenderedTracking(deliveryDates, {
        time_to_get_date: ajaxTime,
        time_to_render_date: renderTime,
        page: isDtf === "true" ? "transfers-pdp" : "blanks-pdp"
      });
    }
  }

  // After selecting new color in the color picker, we should refresh estimation dates
  EventBus.on(Events.PRODUCT_COLOR_SELECTED, async (evt, { color, showEstimates }) => {
    const normalizedSelectedColor = normalizeCompositionName(color);
    if (!normalizedSelectedColor) return;

    selectedColor = normalizedSelectedColor;
    $container.dataset.selectedColor = normalizedSelectedColor;

    const isAssignable = !isDeliveryNonEstimatable(showEstimates);
    $container.dataset.assignable = String(isAssignable);

    if (!isAssignable) return;

    fetchAndUpdateCartShippingInfo(productId, selectedColor);

    CachedDeliveryManager.handleRefreshDeliveryDates({
      containerId: $container.id,
      applyCachedCallback: () => applyCachedDeliveryDate($container),
      fallbackCallback: () =>
        loadDeliveryEstimates({
          productId,
          compositionName: selectedColor,
          useLoader: false
        })
    });
  });

  // After selecting saved cart, we should refresh estimation dates
  EventBus.on(Events.SAVED_CART_SELECTED, async (evt, { number }) => {
    selectedCart = number;
    if (!selectedColor) return;
    await loadDeliveryEstimates({
      productId,
      compositionName: selectedColor,
      orderNumber: selectedCart
    });
  });

  EventBus.on(Events.ZIPCODE_UPDATED, () => {
    if (!selectedColor) return;
    // When zipcode is updated, we need to fetch fresh delivery dates regardless of cache
    // since delivery dates are location-dependent
    loadDeliveryEstimates({
      productId,
      compositionName: selectedColor,
      skipCache: true
    });
  });

  // Depends of count of goods in order delivery dates could be shifted. Checking this case:
  EventBus.on(Events.PRODUCT_INPUT_CHANGED, async ({ target: form }, totalValue, colorName) => {
    const payload = Array.from(form.getElementsByClassName("js-quantity-form-input")).reduce(
      (acc, input) => {
        const quantity = input.value;
        const { variantId } = input.dataset;
        return { ...acc, [variantId]: acc[variantId] || quantity };
      },
      {}
    );

    await loadDeliveryEstimatesForVariant({
      compositionName: colorName,
      orderNumber: selectedCart || "",
      productId,
      variantString: JSON.stringify(payload),
      useLoader: false
    });
  });
};

export default initDates;
